import { useState } from "react";
import { observer } from "mobx-react-lite";
import { CalendarDays, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTran } from "@/hooks";
import { getUnitTypeMeta } from "@/features/organization/utils/organization.helpers";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import OrgBreadcrumb, {
  useOrgPathWithStatus,
} from "@/components/common/OrgBreadcrumb";
import { useDashboardFiltersStore } from "../hooks/useDashboardStore";
import GapGaugeBar from "./GapGaugeBar";
import MetricStatNumbers from "./MetricStatNumbers";

const MetricDetailRow = observer(({ row }) => {
  const { t } = useTran();
  const { available } = useDashboardFiltersStore();
  const [isMonthlyOpen, setIsMonthlyOpen] = useState(false);
  const meta = getUnitTypeMeta(row.unitLevel);
  const Icon = meta.icon;
  const breadcrumb = (row.path || []).join(" > ");
  const unit = row.unit || "";
  const monthlyBreakdown = Array.isArray(row.monthlyBreakdown)
    ? row.monthlyBreakdown
    : [];
  const hasMonthlyBreakdown = monthlyBreakdown.length > 0;
  const showGauge = Number.isFinite(row.target) && Number.isFinite(row.actual);

  const { pathWithStatus, orgStatus, orgInactiveDate } = useOrgPathWithStatus(
    row.orgId,
    available.orgTree,
  );

  return (
    <div className="overflow-hidden rounded-md border border-border/60 bg-card/95">
      <div className="flex flex-col gap-3 p-3 md:flex-row md:items-center md:gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border/80 bg-muted/40",
              meta.color,
            )}
          >
            <Icon className="h-4 w-4" />
          </span>
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-1.5">
              <p className="truncate text-sm font-semibold text-foreground">
                {row.orgName}
              </p>
              <OrgStatusTag
                status={orgStatus}
                inactiveDate={orgInactiveDate}
                className="text-[10px] shrink-0"
              />
            </div>
            <OrgBreadcrumb
              pathWithStatus={pathWithStatus}
              fallbackLabel={breadcrumb}
            />
          </div>
        </div>
        <div className="flex min-w-0 flex-1 justify-end md:pr-12">
          <div className="min-w-0 max-w-[200px] flex-1">
            {showGauge ? <GapGaugeBar gapPct={row.gapPct} /> : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center justify-end gap-2">
          <MetricStatNumbers row={row} unit={unit} />
          {hasMonthlyBreakdown ? (
            <button
              type="button"
              onClick={() => setIsMonthlyOpen((open) => !open)}
              aria-label={isMonthlyOpen ? t("Collapse") : t("Expand")}
              title={isMonthlyOpen ? t("Collapse") : t("Expand")}
              className="inline-flex h-8 w-8 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
            >
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform",
                  isMonthlyOpen && "rotate-180",
                )}
              />
            </button>
          ) : null}
        </div>
      </div>

      {hasMonthlyBreakdown && isMonthlyOpen ? (
        <div className="border-t border-border/60">
          {monthlyBreakdown.map((monthRow) => (
            <div
              key={`${monthRow.year}-${monthRow.month}`}
              className="flex flex-col gap-3 border-b border-border/50 px-3 py-2.5 last:border-b-0 md:flex-row md:items-center md:gap-4"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border/80 bg-primary/5 text-primary">
                  <CalendarDays className="h-4 w-4" />
                </span>
                <p className="truncate text-xs font-semibold text-foreground">
                  {monthRow.label ||
                    `${monthRow.year} - ${String(monthRow.month).padStart(
                      2,
                      "0",
                    )}`}
                </p>
              </div>
              <div className="flex min-w-0 flex-1 justify-end md:pr-12">
                <div className="min-w-0 max-w-[200px] flex-1">
                  {Number.isFinite(monthRow.target) &&
                  Number.isFinite(monthRow.actual) ? (
                    <GapGaugeBar gapPct={monthRow.gapPct} />
                  ) : null}
                </div>
              </div>
              <div className="flex shrink-0 items-center justify-end gap-2">
                <MetricStatNumbers row={monthRow} unit={unit} />
                <span className="h-8 w-8 shrink-0" aria-hidden="true" />
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
});

export default MetricDetailRow;
